import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { useSurvey } from "../hooks/useSurvey";
import {
    QUESTIONS,
    REACTION_TOASTS,
    shouldSkip,
    isQuestionRequired,
} from "../lib/survey-schema";
import { ProgressBar } from "../components/survey/ProgressBar";
import { QuestionCard } from "../components/survey/QuestionCard";
import { CardSelect } from "../components/survey/CardSelect";
import { MultiSelect } from "../components/survey/MultiSelect";
import { ScaleSlider } from "../components/survey/ScaleSlider";
import { TextInput } from "../components/survey/TextInput";
import { EmailInput } from "../components/survey/EmailInput";
import { ReactionToast } from "../components/survey/ReactionToast";
import { Button } from "../components/shared/Button";
import { Logo } from "../components/shared/Logo";
import { Footer } from "../components/shared/Footer";
import { submitSurvey } from "../lib/api";
import { isValidEmail } from "../lib/validation";
import { clearDraft } from "../lib/storage";

const REF_KEY = "klareet_ref";
const REF_RE = /^KLR[A-Z0-9]{5}$/;
const PREFILL_KEY = "klareet_returning_email";
const RETURNING_TOAST_KEY = "klareet_returning_toast";
const SURVEY_PREFILL_KEY = "klareet_survey_email_prefill";

function readStoredRef() {
    try {
        const raw = localStorage.getItem(REF_KEY);
        if (!raw) return null;
        const s = raw.trim().toUpperCase();
        return REF_RE.test(s) ? s : null;
    } catch {
        return null;
    }
}

export default function Survey() {
    const navigate = useNavigate();
    const {
        answers,
        setAnswer,
        toggleTool,
        index,
        nextQuestion,
        previousQuestion,
        total,
    } = useSurvey();

    const [toast, setToast] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [rateLimited, setRateLimited] = useState(false);

    // Pre-fill email if Returning page handed one off (e.g., "Take it again").
    useEffect(() => {
        try {
            const prefill = sessionStorage.getItem(SURVEY_PREFILL_KEY);
            if (prefill && !answers.email) {
                setAnswer("email", prefill);
                sessionStorage.removeItem(SURVEY_PREFILL_KEY);
            }
        } catch {
            // ignore
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (QUESTIONS[index] && shouldSkip(QUESTIONS[index], answers)) {
            nextQuestion();
        }
    }, [index, answers, nextQuestion]);

    const q = QUESTIONS[index];
    const answer = answers[q?.id];

    const canAdvance = useMemo(() => {
        if (!q) return false;
        if (!isQuestionRequired(q, answers)) return true;
        if (q.kind === "card") return Boolean(answer);
        if (q.kind === "scale") return Boolean(answer);
        if (q.kind === "multi") return (answer ?? []).length > 0;
        if (q.kind === "email") return !answer || isValidEmail(answer);
        if (q.kind === "text") return true;
        return true;
    }, [q, answer, answers]);

    const isLast = index === total - 1;

    const buildSubmitPayload = useCallback(() => {
        const {
            persona,
            pain,
            tools,
            gap,
            klareet_appeal,
            price_individual,
            college_price,
            one_thing,
            waitlist,
            email,
        } = answers;
        const payload = {
            persona,
            pain,
            tools: tools ?? [],
            gap: gap ?? null,
            klareet_appeal: Number(klareet_appeal),
            price_individual: price_individual ?? null,
            college_price:
                persona === "professional" ? null : college_price ?? null,
            one_thing: one_thing ? one_thing.trim() : null,
            waitlist,
            email: email ? email.trim() : null,
        };
        const ref = readStoredRef();
        if (ref) payload.referred_by = ref;
        return payload;
    }, [answers]);

    const finishAndGoToGlimpse = useCallback(async () => {
        setSubmitting(true);
        setSubmitError(null);
        const payload = buildSubmitPayload();
        const res = await submitSurvey(payload);
        setSubmitting(false);
        if (res.ok) {
            try {
                sessionStorage.setItem(
                    "klareet_last_answers",
                    JSON.stringify(payload),
                );
                sessionStorage.setItem(
                    "klareet_last_response_id",
                    res.data?.response_id ?? "",
                );
                sessionStorage.setItem(
                    "klareet_last_share_code",
                    res.data?.share_code ?? "",
                );
                sessionStorage.setItem(
                    "klareet_last_email_on_file",
                    payload.email ? "true" : "false",
                );
            } catch {
                // ignore
            }
            clearDraft();
            // Clear the referral so the same device doesn't auto-attribute another response.
            try {
                localStorage.removeItem(REF_KEY);
            } catch {
                // ignore
            }
            const rid = res.data?.response_id;
            const code = res.data?.share_code;
            if (rid && code) {
                navigate(`/snapshot/${rid}?code=${code}&fresh=true`);
            } else {
                navigate("/glimpse");
            }
            return;
        }
        if (res.maxAttempts) {
            // Per-email cap reached. Hand the email + a flash banner to the
            // returning page via react-router state — survives the navigate
            // synchronously and clears on refresh.
            const email = res.data?.email || (payload.email || "").toLowerCase();
            clearDraft();
            navigate("/returning", {
                state: {
                    email,
                    flash:
                        "You have reached the limit of 2 responses for this email. Here are your previous snapshots.",
                },
            });
            return;
        }
        if (res.rateLimited) {
            setRateLimited(true);
            return;
        }
        setSubmitError(
            typeof res.error === "string"
                ? res.error
                : "Something went wrong submitting your response. Try again.",
        );
    }, [buildSubmitPayload, navigate]);

    function onNext() {
        if (!canAdvance) return;
        if (isLast) {
            finishAndGoToGlimpse();
            return;
        }
        if (q.id === "pain" && REACTION_TOASTS.pain[answer]) {
            setToast(REACTION_TOASTS.pain[answer]);
        } else if (q.id === "gap" && REACTION_TOASTS.gap[answer]) {
            setToast(REACTION_TOASTS.gap[answer]);
        }
        nextQuestion();
    }

    function onBack() {
        if (index === 0) {
            navigate("/");
        } else {
            previousQuestion();
        }
    }

    useEffect(() => {
        function handler(e) {
            if (e.key === "Escape") {
                e.preventDefault();
                onBack();
                return;
            }
            if (e.key === "Enter") {
                const isTextarea =
                    e.target instanceof HTMLTextAreaElement ||
                    (q && q.kind === "text");
                if (isTextarea && !(e.metaKey || e.ctrlKey)) {
                    return;
                }
                if (canAdvance) {
                    e.preventDefault();
                    onNext();
                }
            }
        }
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canAdvance, index, q, answers]);

    if (rateLimited) {
        return <RateLimitedScreen />;
    }

    if (!q) return null;

    return (
        <div className="relative min-h-screen klareet-grain klareet-glow">
            <header className="relative z-10 px-5 md:px-12 pt-6 pb-4 flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => navigate("/")}
                    className="flex items-center gap-3 opacity-90 hover:opacity-100 transition-opacity"
                >
                    <Logo className="h-14 md:h-16 w-auto" />
                </button>
                <div className="hidden md:block text-[11px] uppercase tracking-[0.22em] font-mono text-klareet-MUT">
                    Press <kbd className="mx-1 px-1.5 py-0.5 rounded border border-klareet-bor text-klareet-TEXT font-mono text-[10px]">Enter</kbd> to advance
                </div>
            </header>

            <div className="relative z-10 px-5 md:px-12 max-w-3xl mx-auto pt-2">
                <ProgressBar value={index + 1} total={total} />
            </div>

            <ReactionToast
                text={toast}
                show={Boolean(toast)}
                onDone={() => setToast(null)}
            />

            <main key={q.id} className="relative z-10 px-5 md:px-12 pb-32">
                <QuestionCard
                    tag={q.tag}
                    question={q.question}
                    subtitle={q.subtitle}
                >
                    {q.kind === "card" ? (
                        <CardSelect
                            options={q.options}
                            value={answer}
                            onChange={(v) => setAnswer(q.id, v)}
                            name={q.id}
                        />
                    ) : null}

                    {q.kind === "multi" ? (
                        <MultiSelect
                            options={q.options}
                            values={answer}
                            onToggle={(v) => toggleTool(v)}
                            name={q.id}
                        />
                    ) : null}

                    {q.kind === "scale" ? (
                        <ScaleSlider
                            value={answer}
                            onChange={(v) => setAnswer(q.id, v)}
                            reactions={q.reactions}
                            lo={q.lo}
                            hi={q.hi}
                        />
                    ) : null}

                    {q.kind === "text" ? (
                        <TextInput
                            value={answer ?? ""}
                            onChange={(v) => setAnswer(q.id, v)}
                            placeholder={q.placeholder}
                            maxLength={q.maxLength}
                            onSubmit={onNext}
                        />
                    ) : null}

                    {q.kind === "email" ? (
                        <EmailInput
                            value={answer ?? ""}
                            onChange={(v) => setAnswer(q.id, v)}
                        />
                    ) : null}
                </QuestionCard>
            </main>

            <div className="fixed bottom-0 inset-x-0 z-20 bg-gradient-to-t from-klareet-NAVY via-klareet-NAVY/95 to-transparent pt-10 pb-6">
                <div className="max-w-3xl mx-auto px-5 md:px-12 flex items-center justify-between gap-3">
                    <Button
                        variant="ghost"
                        size="md"
                        onClick={onBack}
                        data-testid="back-btn"
                    >
                        <ArrowLeft size={16} />
                        Back
                    </Button>
                    <div className="flex items-center gap-3">
                        {submitError ? (
                            <span
                                className="text-sm text-klareet-RED font-mono"
                                data-testid="submit-error"
                            >
                                {submitError}
                            </span>
                        ) : null}
                        {q.optional &&
                        (q.kind === "text" || q.kind === "email") ? (
                            <Button
                                variant="ghost"
                                size="md"
                                onClick={() => {
                                    setAnswer(q.id, q.kind === "text" ? "" : "");
                                    if (isLast) {
                                        finishAndGoToGlimpse();
                                    } else {
                                        nextQuestion();
                                    }
                                }}
                                data-testid="skip-btn"
                            >
                                Skip
                            </Button>
                        ) : null}
                        <Button
                            onClick={onNext}
                            disabled={!canAdvance || submitting}
                            size="md"
                            data-testid="next-btn"
                        >
                            {isLast ? (
                                submitting ? (
                                    <>Submitting…</>
                                ) : (
                                    <>
                                        Finish
                                        <CheckCircle2 size={16} />
                                    </>
                                )
                            ) : (
                                <>
                                    Next
                                    <ArrowRight size={16} />
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
}

function RateLimitedScreen() {
    const navigate = useNavigate();
    return (
        <div className="relative min-h-screen klareet-grain klareet-glow grid place-items-center px-5">
            <div
                className="max-w-lg text-center rounded-3xl border border-klareet-AMBER/40 bg-klareet-card p-10"
                data-testid="rate-limited"
            >
                <div className="text-5xl mb-4">🕊️</div>
                <h2 className="text-2xl font-semibold tracking-tight text-klareet-TEXT">
                    You've already responded in the last 24 hours.
                </h2>
                <p className="mt-3 text-klareet-MUT leading-relaxed">
                    One voice per person keeps the signal clean. Come back
                    tomorrow if you'd like to add more. We'd love to hear it.
                </p>
                <div className="mt-6 flex justify-center gap-3">
                    <Button
                        size="md"
                        variant="outline"
                        onClick={() => navigate("/results")}
                    >
                        See how others responded
                    </Button>
                    <Button size="md" onClick={() => navigate("/")}>
                        Home
                    </Button>
                </div>
            </div>
        </div>
    );
}
